/**
 * The standard road bike everybody rides in VeloStube. These values are fixed on purpose: they are not
 * settings, so every rider is simulated on identical equipment (fair group rides, comparable times).
 *
 * CdA 0.36 m²: drag area of a road rider with the hands on the brake hoods — the usual position on a trainer.
 * Published ranges are ≈ 0.32–0.40 m² on the hoods and ≈ 0.30–0.35 m² in the drops (Wilson, Bicycling
 * Science; bike-fit/aero literature). 0.36 sits in the middle of the hoods range.
 */

/** Virtual gear range (geometric progression, SPEC §5.2). */
export interface GearConfig {
  count: number;
  minRatio: number;
  maxRatio: number;
}

/** Draft model parameters (SPEC §9.4). */
export interface DraftConfig {
  fullGapM: number;
  zeroGapM: number;
  maxReduction: number;
  multiBonus: number;
  cap: number;
}

export const BIKE = {
  /** Complete bike incl. pedals and bottles (kg). */
  massKg: 7.2,
  /** Drag area, hands on the hoods (m²). */
  cda: 0.36,
  /** 700×25C (ETRTO 25-622) rolling circumference (m). */
  wheelCircumferenceM: 2.105,
  /** Physical gear on the trainer: big ring of a 50/34 crankset on the 14T Zwift Cog. */
  chainringTeeth: 50,
  cogTeeth: 14,
} as const;

/** 24 virtual gears from 0.75 to 4.8 (≈ 34×45 … 53×11). */
export const GEARS: Readonly<GearConfig> = { count: 24, minRatio: 0.75, maxRatio: 4.8 };

/** Full 30 % reduction up to 1.5 m behind a rider, none from 8 m; +5 % for a group ahead, max 40 %. */
export const DRAFT: Readonly<DraftConfig> = {
  fullGapM: 1.5,
  zeroGapM: 8,
  maxReduction: 0.3,
  multiBonus: 0.05,
  cap: 0.4,
};

/** Rider + bike mass (kg). */
export function systemMassKg(riderWeightKg: number): number {
  return riderWeightKg + BIKE.massKg;
}
